import { defineStore } from 'pinia';
import api from '@/services/api.service';
import type { Customer } from '@/types/customer.types';
import type { Pagination } from '@/types/common.types';
import { getApiErrorMessage } from '@/utils/apiError';

interface CustomerState {
  customers: Customer[];
  searchResults: Customer[];
  pagination: Pagination | null;
  isLoading: boolean;
  error: string | null;
}

interface FetchCustomersParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'deleted';
}

export const useCustomerStore = defineStore('customer', {
  state: (): CustomerState => ({
    customers: [],
    searchResults: [],
    pagination: null,
    isLoading: false,
    error: null,
  }),

  actions: {
    async fetchCustomers(params: FetchCustomersParams = {}) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await api.get('/customers', { params });
        this.customers = response.data.data.customers;
        this.pagination = response.data.pagination;
      } catch (error) {
        this.error = getApiErrorMessage(error, 'Failed to fetch customers');
      } finally {
        this.isLoading = false;
      }
    },
    async addCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'>) {
      this.isLoading = true;
      this.error = null;
      try {
        await api.post('/customers', customerData);
        // Refresh the list to show the new customer
        await this.fetchCustomers({ page: this.pagination?.currentPage, limit: this.pagination?.pageSize });
      } catch (error) {
        this.error = getApiErrorMessage(error, 'Failed to add customer');
        throw error; // Re-throw to be caught in the component
      } finally {
        this.isLoading = false;
      }
    },

    async updateCustomer(id: string, customerData: Partial<Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'>>) {
      this.isLoading = true;
      this.error = null;
      try {
        await api.put(`/customers/${id}`, customerData);
        await this.fetchCustomers({ page: this.pagination?.currentPage, limit: this.pagination?.pageSize });
      } catch (error) {
        this.error = getApiErrorMessage(error, 'Failed to update customer');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async deleteCustomer(id: string) {
      this.isLoading = true;
      this.error = null;
      try {
        await api.delete(`/customers/${id}`);
        // After deleting, we might be on a page that no longer exists.
        // A simple approach is to go back to the first page.
        await this.fetchCustomers({ page: 1, limit: this.pagination?.pageSize });
      } catch (error) {
        this.error = getApiErrorMessage(error, 'Failed to delete customer');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async searchCustomers(params: { q: string }) {
      this.isLoading = true;
      this.error = null;
      try {
        // Note: This uses a GET request with a query parameter.
        // Ensure your backend is set up to handle `GET /customers/search?phone=...`
        const response = await api.get('/customers/search', { params });
        this.searchResults = response.data.data;
      } catch (error) {
        this.searchResults = [];
        this.error = getApiErrorMessage(error, 'Failed to search for customers');
      } finally {
        this.isLoading = false;
      }
    },

    async searchCustomerByPhone(phoneNumber: string) {
      this.isLoading = true;
      this.error = null;
      try {
        // Calls the public API endpoint for searching by phoneNumber number
        const response = await api.get('/customers/search-by-phone', { params: { phoneNumber } });
        // Assuming response.data contains an array of customers
        this.searchResults = response.data?.data || [];
      } catch (error) {
        this.searchResults = [];
        this.error = getApiErrorMessage(error, 'Failed to search for customer by phone');
      } finally {
        this.isLoading = false;
      }
    },
//
  },
});